import type { TFunction } from 'i18next';
/**
 * The side nav's top-level entries, and the keys a stored `navConfig` names.
 * Entries the shell only adds for some tenants are left out on purpose.
 */

/** Every top-level key, in the order the app shows them out of the box. */
export const NAV_ITEM_KEYS = [
  'home',
  'alerts',
  'risks',
  'findings',
  'assets',
  'agent-security',
  'dspm',
  'cwpp',
  'compliance',
  'ciem',
  'threat-map',
  'code-security',
  'settings',
] as const;

export type NavItemKey = (typeof NAV_ITEM_KEYS)[number];

/**
 * Plain strings: a name is the item's id in the DOM and in analytics, and
 * what the server's nav config and the group picker refer to. The shown
 * text comes from `navItemLabel` where the name is rendered.
 */
export const NAV_ITEM_LABELS: Record<NavItemKey, string> = {
  home: 'Home',
  alerts: 'Alerts',
  risks: 'Top risks',
  findings: 'Findings',
  assets: 'Assets',
  'agent-security': 'Agent security',
  dspm: 'Data security',
  cwpp: 'Vulnerabilities',
  compliance: 'Compliance',
  ciem: 'Entitlements',
  'threat-map': 'Threat mapping',
  'code-security': 'Code security',
  settings: 'Settings',
};

export type NavItemDescriptor = {
  key: NavItemKey;
  name: string;
};

/** The catalog: what can be reordered or hidden, in default order. */
export const NAV_ITEMS: NavItemDescriptor[] = NAV_ITEM_KEYS.map((key) => ({
  key,
  name: NAV_ITEM_LABELS[key],
}));

/**
 * The entry the shell renders only for tenants with an AWS integration,
 * appended at render time rather than living in the catalog.
 *
 * It is kept out of `NAV_ITEMS` because the side nav builds itself from
 * that list, and a tenant without AWS should not grow an entry it has no
 * page for.
 */
export const AWS_ONLY_NAV_ITEMS: NavItemDescriptor[] = [
  { key: 'well-architected' as NavItemKey, name: 'Well-Architected' },
];

/**
 * What the group navigation picker offers.
 *
 * Wider than the catalog the nav builds from: the nav orders and hides by
 * key, and that key reaches it whenever the tenant has AWS, so an admin
 * should be able to arrange it. A tenant without AWS can drag it and see
 * nothing change, which costs less than an AWS admin being unable to move
 * one of their own entries.
 */
export const NAV_PICKER_ITEMS: NavItemDescriptor[] = [
  ...NAV_ITEMS,
  ...AWS_ONLY_NAV_ITEMS,
];

/** The `common` key for each nav name, sub-items included. */
export const NAV_LABEL_KEYS: Record<string, string> = {
  Home: 'navHome',
  Alerts: 'navAlerts',
  'Top risks': 'navTopRisks',
  Findings: 'navFindings',
  Assets: 'navAssets',
  'Data security': 'navDataSecurity',
  Vulnerabilities: 'navVulnerabilities',
  Compliance: 'navCompliance',
  Entitlements: 'navEntitlements',
  'Threat mapping': 'navThreatMapping',
  'Code security': 'navCodeSecurity',
  Settings: 'navSettings',
  Overview: 'navOverview',
  Integrations: 'navIntegrations',
  Environments: 'navEnvironments',
  Profiles: 'navProfiles',
  'Workload scanning policy': 'navWorkloadScanningPolicy',
  'Finding SLAs': 'navFindingSlas',
  Workflows: 'navWorkflows',
  'Custom reports': 'navCustomReports',
  'API keys': 'navApiKeys',
  'Partner single sign-on': 'navPartnerSingleSignOn',
  'Asset groups': 'navAssetGroups',
  'Compliance frameworks': 'navComplianceFrameworks',
  'Tenant Settings': 'navTenantSettings',
  'Well-Architected frameworks': 'navWellArchitectedFrameworks',
  'Well-Architected': 'navWellArchitected',
  Pleri: 'navPleri',
  'Custom checks': 'navCustomChecks',
  'Integration groups': 'navIntegrationGroups',
};

/** What a nav item shows for its name; an unlisted name shows as it is. */
export const navItemLabel = (t: TFunction<'common'>, name: string): string => {
  const key = NAV_LABEL_KEYS[name];
  return key ? t(key) : name;
};
